import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CloudArrowUp, Trash, Warning, CheckCircle, Database } from "@phosphor-icons/react";
import { fmtNumber } from "@/lib/format";

function SummaryStat({ label, value, accent, testId }) {
  return (
    <div className={`border p-3 ${accent || "border-canvas-line bg-canvas-card"}`} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-display text-xl font-bold mt-1 tabular-nums text-ink">{value}</div>
    </div>
  );
}

function DryRunReport({ data }) {
  if (!data) return null;
  const s = data.summary || {};
  const u = s.unique || {};
  return (
    <div className="space-y-3" data-testid="dry-run-report">
      <div className="border border-[#00B341] bg-[#00B341]/5 p-3 flex items-start gap-2">
        <CheckCircle size={16} weight="bold" className="text-[#00B341] shrink-0 mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold">Parsed sheet:</span> {s.sheet} ·{" "}
          <span className="font-semibold">{fmtNumber(s.valid_sales || 0)}</span> valid sales from{" "}
          {fmtNumber(s.total_rows || 0)} rows ·{" "}
          <span className="text-destructive">{fmtNumber(s.errors_count || 0)} skipped</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <SummaryStat label="Branches" value={u.branches || 0} testId="dryrun-branches" />
        <SummaryStat label="Sales Agents" value={u.sales_agents || 0} testId="dryrun-agents" />
        <SummaryStat label="Financiers" value={u.financiers || 0} testId="dryrun-financiers" />
        <SummaryStat label="Packages" value={u.packages || 0} testId="dryrun-packages" />
        <SummaryStat label="Customers" value={fmtNumber(u.customers || 0)} testId="dryrun-customers" />
        <SummaryStat label="Vehicles" value={fmtNumber(u.vehicles || 0)} testId="dryrun-vehicles" />
      </div>

      {(s.sample_branches || []).length > 0 && (
        <details className="border border-canvas-line bg-canvas-card">
          <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted cursor-pointer hover:bg-canvas-panel">
            Detected branches ({s.sample_branches.length})
          </summary>
          <div className="px-3 py-2 text-xs">{s.sample_branches.join(" · ")}</div>
        </details>
      )}
      {(s.sample_financiers || []).length > 0 && (
        <details className="border border-canvas-line bg-canvas-card">
          <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted cursor-pointer hover:bg-canvas-panel">
            Detected financiers ({s.sample_financiers.length})
          </summary>
          <div className="px-3 py-2 text-xs">{s.sample_financiers.join(" · ")}</div>
        </details>
      )}
      {(s.sample_agents || []).length > 0 && (
        <details className="border border-canvas-line bg-canvas-card">
          <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted cursor-pointer hover:bg-canvas-panel">
            Sample sales agents ({s.sample_agents.length})
          </summary>
          <div className="px-3 py-2 text-xs">{s.sample_agents.join(" · ")}</div>
        </details>
      )}
      {(s.errors_preview || []).length > 0 && (
        <details className="border border-destructive/40 bg-destructive/5">
          <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-destructive cursor-pointer">
            Errors preview ({s.errors_preview.length} of {s.errors_count})
          </summary>
          <table className="w-full text-xs">
            <thead className="bg-canvas-panel">
              <tr>
                <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Row</th>
                <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Reason</th>
              </tr>
            </thead>
            <tbody>
              {s.errors_preview.map((e, i) => (
                <tr key={i} className="border-t border-canvas-line">
                  <td className="px-3 py-1.5 font-mono">{e.row}</td>
                  <td className="px-3 py-1.5">{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function CommitReport({ data }) {
  if (!data) return null;
  const c = data.created || {};
  return (
    <div className="border border-[#00B341] bg-[#00B341]/5 p-4" data-testid="commit-report">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle size={18} weight="bold" className="text-[#00B341]" />
        <h4 className="font-display text-base font-semibold">Migration committed</h4>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        {Object.entries(c).map(([k, v]) => (
          <div key={k} className="border border-canvas-line bg-canvas-card p-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{k}</div>
            <div className="font-display text-lg font-bold tabular-nums">{fmtNumber(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DataMigrationPanel() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [showWipe, setShowWipe] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");

  // Prep-for-reimport (transactional-only) state
  const [prepping, setPrepping] = useState(false);
  const [showPrep, setShowPrep] = useState(false);
  const [prepConfirm, setPrepConfirm] = useState("");

  const upload = async (mode) => {
    if (!file) {
      toast.error("Select an Excel (.xlsx) file first.");
      return;
    }
    setLoading(true);
    setCommitted(null);
    if (mode === "dry-run") setDryRun(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      const { data } = await api.post("/migration/excel-sales", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 min for large files
      });
      if (mode === "dry-run") {
        setDryRun(data);
        toast.success(`Parsed ${data.summary?.valid_sales || 0} sales — review and commit`);
      } else {
        setCommitted(data);
        toast.success("Data committed successfully");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const wipe = async () => {
    if (wipeConfirm !== "WIPE-ALL-DATA") {
      toast.error("Type exactly 'WIPE-ALL-DATA' to proceed.");
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post("/migration/wipe", {
        confirm: wipeConfirm,
        wipe_masters: true,
      });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Wiped ${total} records across ${Object.keys(data.deleted || {}).length} collections`);
      setShowWipe(false);
      setWipeConfirm("");
      // Optional: reload page so cached lists clear
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWiping(false);
    }
  };

  const prepReimport = async () => {
    if (prepConfirm !== "PREP-REIMPORT") {
      toast.error("Type exactly 'PREP-REIMPORT' to proceed.");
      return;
    }
    setPrepping(true);
    try {
      const { data } = await api.post("/migration/prep-reimport", { confirm: prepConfirm });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Cleared ${total} transactional records. Masters preserved.`);
      setShowPrep(false);
      setPrepConfirm("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPrepping(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="data-migration-panel">
      {/* PREP-FOR-REIMPORT BLOCK (preferred over full wipe) */}
      <div className="border-2 border-ink bg-canvas-card" data-testid="prep-reimport-block">
        <div className="px-4 py-3 border-b border-canvas-line flex items-center gap-2">
          <Trash size={18} weight="bold" className="text-ink" />
          <h3 className="font-display text-base font-semibold">
            Step 0 — Prepare for Re-Import (recommended)
          </h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p>
            Clears <strong>only transactional data</strong> so you can run a clean re-import from
            another system. Deleted: sales, vehicles, bookings, vouchers, registrations, customers,
            commissions, finance/exchange/platform receivables, discount buckets, audit logs,
            transfers, counters.
          </p>
          <p className="text-ink-muted">
            <strong>Preserved:</strong> users, roles, branches, branch types, packages, financiers,
            evaluators, sales agents, platforms, dealership settings.
          </p>
          {!showPrep ? (
            <button
              onClick={() => setShowPrep(true)}
              data-testid="open-prep-reimport"
              className="px-3 py-2 border-2 border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white inline-flex items-center gap-2"
            >
              <Warning size={12} weight="bold" />
              Reveal prep action
            </button>
          ) : (
            <div className="border border-canvas-line bg-canvas-panel p-3 space-y-2">
              <div className="font-mono text-[11px]">
                Type <code className="font-bold">PREP-REIMPORT</code> to confirm:
              </div>
              <input
                value={prepConfirm}
                onChange={(e) => setPrepConfirm(e.target.value)}
                placeholder="PREP-REIMPORT"
                data-testid="prep-confirm-input"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={prepReimport}
                  disabled={prepping || prepConfirm !== "PREP-REIMPORT"}
                  data-testid="confirm-prep"
                  className="px-3 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-50"
                >
                  {prepping ? "Clearing…" : "Clear Transactional Data"}
                </button>
                <button
                  onClick={() => { setShowPrep(false); setPrepConfirm(""); }}
                  className="px-3 py-2 border border-canvas-line text-xs hover:border-ink"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* WIPE BLOCK */}
      <div className="border-2 border-destructive bg-destructive/5">
        <div className="px-4 py-3 border-b border-destructive/30 flex items-center gap-2">
          <Trash size={18} weight="bold" className="text-destructive" />
          <h3 className="font-display text-base font-semibold text-destructive">
            Step 1 — Wipe demo / test data
          </h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p>
            Removes all <strong>sales, bookings, vouchers, vehicles, customers, transfers, audit
            logs, counters</strong>, plus all <strong>packages, financiers, evaluators, branches</strong>
            (except <code>Stock Yard</code>) and <strong>non-placeholder sales agents</strong>.
            <br />
            <span className="text-ink-muted">Preserved: users, roles, branch types, dealership settings, Stock Yard branch.</span>
          </p>
          {!showWipe ? (
            <button
              onClick={() => setShowWipe(true)}
              data-testid="open-wipe"
              className="px-3 py-2 border-2 border-destructive text-destructive text-xs font-semibold hover:bg-destructive hover:text-white inline-flex items-center gap-2"
            >
              <Warning size={12} weight="bold" />
              Reveal wipe action
            </button>
          ) : (
            <div className="border border-destructive/40 bg-canvas-card p-3 space-y-2">
              <div className="font-mono text-[11px] text-destructive">
                Type <code className="font-bold">WIPE-ALL-DATA</code> to confirm:
              </div>
              <input
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                placeholder="WIPE-ALL-DATA"
                data-testid="wipe-confirm-input"
                className="w-full px-3 py-2 bg-canvas border border-destructive text-sm font-mono focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={wipe}
                  disabled={wiping || wipeConfirm !== "WIPE-ALL-DATA"}
                  data-testid="confirm-wipe"
                  className="px-3 py-2 bg-destructive text-white text-xs font-semibold hover:bg-destructive/80 disabled:opacity-50"
                >
                  {wiping ? "Wiping…" : "Wipe Data Now"}
                </button>
                <button
                  onClick={() => {
                    setShowWipe(false);
                    setWipeConfirm("");
                  }}
                  className="px-3 py-2 border border-canvas-line text-xs hover:border-ink"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* IMPORT BLOCK */}
      <div className="border-2 border-ink">
        <div className="px-4 py-3 border-b border-canvas-line flex items-center gap-2">
          <Database size={18} weight="bold" className="text-ink" />
          <h3 className="font-display text-base font-semibold">
            Step 2 — Import historical sales from Excel
          </h3>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div>
            <p>
              Expects a sheet with columns matching the dealership <code>CLEANSED_DATA</code> format
              — Date, Doc ID, Branch Name, Executive Name, Customer Name, Phone Number, Chasis Number,
              Engine Number, Vehicle Model, Purchase Mode (Cash/Finance), Financier Name, prices, and
              payment splits (Through Cash / Through Online / Finance Amount).
            </p>
            <p className="text-ink-muted mt-2">
              <strong>Run dry-run first.</strong> Review the counts and any errors, then commit.
              All historical finance & exchange amounts are marked as <code>received</code>.
            </p>
            <p className="text-destructive mt-2">
              <strong>Strict integrity:</strong> every row&apos;s <code>Vehicle Model</code> must match
              an EXISTING ACTIVE package. If any VIN row fails this check, the entire file is
              rejected. Seed the required packages on the Packages page first.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <label className="px-3 py-2 border-2 border-dashed border-canvas-line cursor-pointer hover:border-ink inline-flex items-center gap-2">
              <CloudArrowUp size={14} weight="bold" />
              <span className="text-xs font-semibold">
                {file ? file.name : "Choose .xlsx file…"}
              </span>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setDryRun(null);
                  setCommitted(null);
                }}
                data-testid="excel-file-input"
                className="hidden"
              />
            </label>
            <button
              onClick={() => upload("dry-run")}
              disabled={!file || loading}
              data-testid="dry-run-btn"
              className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white disabled:opacity-50"
            >
              {loading ? "Parsing…" : "Run Dry-Run"}
            </button>
            <button
              onClick={() => upload("commit")}
              disabled={!file || !dryRun || loading}
              data-testid="commit-btn"
              className="px-3 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-50"
              title={!dryRun ? "Run dry-run first" : "Commit to DB"}
            >
              {loading ? "Committing…" : "Commit to Database"}
            </button>
          </div>

          <DryRunReport data={dryRun} />
          <CommitReport data={committed} />
        </div>
      </div>
    </div>
  );
}
